/**
 * A hand-rolled icon set. No icon library: the console needs ~20 glyphs and a
 * dependency that ships 1,500 of them is 1,480 shapes of someone else's taste.
 *
 * All icons are 24×24, 1.6 stroke, `currentColor`, round caps — so they sit
 * with Bricolage's round geometry rather than fighting it.
 */

import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 20, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconHelp = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.2a2.6 2.6 0 1 1 3.2 2.5c-.6.2-.9.7-.9 1.3v.5" />
    <path d="M12 16.6h.01" />
  </Icon>
);

export const IconOverview = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="3" width="8" height="8" rx="2.5" />
    <rect x="13" y="3" width="8" height="5" rx="2.5" />
    <rect x="3" y="13" width="8" height="8" rx="2.5" />
    <rect x="13" y="10" width="8" height="11" rx="2.5" />
  </Icon>
);

export const IconChat = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20 12.5a7.5 7.5 0 0 1-10.9 6.7L4 20.5l1.4-4.6A7.5 7.5 0 1 1 20 12.5Z" />
  </Icon>
);

export const IconSessions = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 6.5h16M4 12h11M4 17.5h7" />
  </Icon>
);

export const IconApprovals = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3.2 20 6.4v5.1c0 4.3-3.2 8.1-8 9.3-4.8-1.2-8-5-8-9.3V6.4Z" />
    <path d="m8.8 12.1 2.2 2.2 4.3-4.4" />
  </Icon>
);

export const IconAutomations = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3v3m0 12v3M3 12h3m12 0h3M5.6 5.6l2.1 2.1m8.6 8.6 2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
    <circle cx="12" cy="12" r="3.2" />
  </Icon>
);

export const IconIntegrations = (p: IconProps) => (
  <Icon {...p}>
    <path d="M10.5 13.5 8 16a3.5 3.5 0 0 1-5-5l2.5-2.5" />
    <path d="M13.5 10.5 16 8a3.5 3.5 0 0 1 5 5l-2.5 2.5" />
    <path d="M9.5 14.5 14.5 9.5" />
  </Icon>
);

export const IconAudit = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 3.5h8.5L19 8v12.5H6Z" />
    <path d="M14 3.5V8h5" />
    <path d="M9 13h6M9 16.5h4" />
  </Icon>
);

export const IconPeople = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="9" cy="8.5" r="3.3" />
    <path d="M3.5 20c0-3.2 2.5-5.4 5.5-5.4s5.5 2.2 5.5 5.4" />
    <path d="M16 5.6a3.3 3.3 0 0 1 0 6.4M17.5 14.9c1.8.6 3 2.5 3 5.1" />
  </Icon>
);

export const IconUsage = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 19.5h16" />
    <path d="M7 19.5V12M12 19.5V6.5M17 19.5v-5" />
  </Icon>
);

export const IconSkills = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3.2 14.6 9l6.2.5-4.7 4 1.4 6-5.5-3.3L6.5 19.5l1.4-6-4.7-4L9.4 9Z" />
  </Icon>
);

export const IconMemory = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9 4.5a3.5 3.5 0 0 0-3.5 3.5A3 3 0 0 0 4 13a3.2 3.2 0 0 0 2.3 3.1A3.2 3.2 0 0 0 12 18V6a3 3 0 0 0-3-1.5Z" />
    <path d="M15 4.5A3.5 3.5 0 0 1 18.5 8 3 3 0 0 1 20 13a3.2 3.2 0 0 1-2.3 3.1A3.2 3.2 0 0 1 12 18" />
  </Icon>
);

export const IconArtifacts = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="4.5" width="17" height="15" rx="3" />
    <path d="M3.5 9.5h17" />
    <path d="M7 14h5" />
  </Icon>
);

export const IconUtilisation = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 3.5v8.5l5.5 3.5" />
  </Icon>
);

export const IconSettings = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 14.5a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.3a2 2 0 1 1-4 0v-.2a1.6 1.6 0 0 0-2.8-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7h-.3a2 2 0 1 1 0-4h.2a1.6 1.6 0 0 0 1.1-2.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 2.7-1.1v-.3a2 2 0 1 1 4 0v.2a1.6 1.6 0 0 0 2.8 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.3a2 2 0 1 1 0 4h-.2a1.6 1.6 0 0 0-1.4 1Z" />
  </Icon>
);

export const IconBench = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9.5 3.5v6L4.8 18a2.4 2.4 0 0 0 2.1 3.5h10.2a2.4 2.4 0 0 0 2.1-3.5l-4.7-8.5v-6" />
    <path d="M8 3.5h8M7.5 14.5h9" />
  </Icon>
);

export const IconFleet = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 20V9.5l6-4 6 4V20" />
    <path d="M15.5 20V12l5 3.2V20" />
    <path d="M2.5 20h19M8 20v-4h3v4" />
  </Icon>
);

export const IconIncidents = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3.8 21 19.5H3Z" />
    <path d="M12 9.8v4.4M12 17.2h.01" />
  </Icon>
);

export const IconReleases = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 7.5 12 3.5l8 4v9L12 20.5l-8-4Z" />
    <path d="m4 7.5 8 4 8-4M12 11.5v9" />
  </Icon>
);

export const IconBilling = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="5.5" width="18" height="13" rx="3" />
    <path d="M3 10h18M7 14.5h3" />
  </Icon>
);

export const IconOrgs = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="3.5" width="9" height="17" rx="2" />
    <path d="M13 9h6.5a1.5 1.5 0 0 1 1.5 1.5v10H13" />
    <path d="M7.2 7.5h2.6M7.2 11h2.6M7.2 14.5h2.6M16 13h2M16 16.5h2" />
  </Icon>
);

export const IconInbox = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 13.5 6 5.2h12l2.5 8.3v5.3h-17Z" />
    <path d="M3.5 13.5h4l1 2.5h7l1-2.5h4" />
  </Icon>
);

export const IconAlert = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5v5M12 16h.01" />
  </Icon>
);

export const IconWrench = (p: IconProps) => (
  <Icon {...p}>
    <path d="M15.4 3.6a5 5 0 0 0-5.9 6.6L3.6 16a2.2 2.2 0 1 0 3.1 3.1l5.8-5.8a5 5 0 0 0 6.6-5.9l-3 3-2.7-.7-.7-2.7Z" />
  </Icon>
);

export const IconClose = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 6 18 18M18 6 6 18" />
  </Icon>
);

export const IconChevronDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6 9.5 6 5.5 6-5.5" />
  </Icon>
);

export const IconCheck = (p: IconProps) => (
  <Icon {...p}>
    <path d="m5 12.5 4.5 4.5L19 7" />
  </Icon>
);

export const IconSend = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20.5 3.5 3.5 10.2l7.1 2.9 2.6 7.4Z" />
    <path d="M10.6 13.1 20.5 3.5" />
  </Icon>
);

export const IconStop = (p: IconProps) => (
  <Icon {...p}>
    <rect x="6" y="6" width="12" height="12" rx="3" />
  </Icon>
);

export const IconShield = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3.2 20 6.4v5.1c0 4.3-3.2 8.1-8 9.3-4.8-1.2-8-5-8-9.3V6.4Z" />
  </Icon>
);

export const IconExternal = (p: IconProps) => (
  <Icon {...p}>
    <path d="M14 4.5h5.5V10" />
    <path d="M19.5 4.5 11 13" />
    <path d="M18 14.5v4a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6h4" />
  </Icon>
);
